import { Button, Dialog, Intent } from "@blueprintjs/core";
import assertNever from "assert-never";
import * as React from "react";
import "./GenericSubSection.css";
import { match, Route } from "react-router";
import { HRSubSectionWithStatus } from "../models/HR";
import { HRQuestionId, HRSubSectionId, CertificationSettings, HRCertificationPayRatesWithSupplementals, SubsectionDocumentData, HRAnswerResult } from "../messages";
import { CaregiverCertification, HRQuestionGroupId } from "../messages/ObjectIds";
import { GenericQuestionGroupList } from "./GenericQuestionGroupList";
import Element from "react-scroll/modules/components/Element";
import { API } from "../API";
import { isLoading } from "../Loading";
import "./GenericSubSection.css";
import SubSectionLinkComponent from "./widgets/SubSectionLinkComponent";
import { incrementTestTriesForCaregiver, getTestTriesForCaregiver } from "../LocalStorageManager";
import DocumentOverlayActivity from "src/activities/DocumentOverlayActivity";

interface GenericSubSectionProps {
    subSectionDetails: HRSubSectionWithStatus;
    matchUrl: string;
    // TODO give type to newval
    onAnswerChange: (newVal: any, questionId: HRQuestionId, groupId: HRQuestionGroupId) => void;
    onAnswerBlur: (questionId: HRQuestionId, groupId: HRQuestionGroupId) => void;
    onTriggerEvaluateHierarchyStatus: (questionId: HRQuestionId, groupId: HRQuestionGroupId) => void;
    getCertificationPayRates: () => CertificationSettings;
    isClicked: boolean;
    onLinkToSubSectionClick: (subSectionId: HRSubSectionId) => void;
    onNextButtonClick: () => void;
    onDocumentGeneratedStatusChange: (subSectionId: HRSubSectionId, isGenerated: boolean) => void;
    onTestTryAgain: (subSectionId: HRSubSectionId) => void;
    api: API;
    selectedCertification: CaregiverCertification;
}

interface GenericSubSectionState {
    showUnfinishedError: { errors: number, loading: number } | null;
    showDocumentPopUp: SubsectionDocumentData | null;

    // TODO remove when we go back to popup
    documentUploadStatus: "LOADING" | "ERROR" | null;
    showTestPopUp: { type: "FAIL" | "SUCCESS" | "MORE_TRIES", text: string } | null;
}

export class GenericSubSection extends React.Component<GenericSubSectionProps, GenericSubSectionState> {
    constructor(props: GenericSubSectionProps) {
        super(props);

        this.state = {
            showUnfinishedError: null,
            showDocumentPopUp: null,
            showTestPopUp: null,
            documentUploadStatus: null
        };
    }

    handleAnswerChange(newVal: any, questionId: HRQuestionId, groupId: HRQuestionGroupId) {
        this.setState({ showUnfinishedError: null });
        this.props.onAnswerChange(newVal, questionId, groupId);
    }

    async submitCaregiverCertificationPayRates (rates: HRCertificationPayRatesWithSupplementals): Promise<HRAnswerResult> {
        return this.props.api.putSubmitCaregiverCertificationPayRates(this.props.subSectionDetails.data.id, rates);
    }

    async handleNextButtonClick() {
        if (!this.props.subSectionDetails.mandatoryCompleted) {
            let errorCounter = 0;
            let loadingCounter = 0;
            this.props.subSectionDetails.groups.map(group => {
                if (!group.mandatoryCompleted) {
                    group.questions.map(q => {
                        if (!q.question.isHidden) {
                            if (q.status !== null) {
                                if (q.status === false && q.question.isMandatory) {
                                    errorCounter++;
                                } else if (isLoading(q.status)) {
                                    loadingCounter++;
                                }
                            } else if (q.status === null && q.question.isMandatory) {
                                this.props.onAnswerBlur(q.question.id, group.data.id);
                                errorCounter++;
                            }
                        }
                    });
                }
            });
            this.setState({ showUnfinishedError: { loading: loadingCounter, errors: errorCounter } });
        } else {
            let wrongAnswersCounter = 0;
            let testQuestionCounter = 0;
            this.props.subSectionDetails.groups.map(group => {
                group.questions.map(q => {
                    if (q.question.type === "AmericanTest") {
                        testQuestionCounter++;
                        if (q.question.chosenAnswerId !== q.question.correctAnswer) {
                            wrongAnswersCounter++;
                        }
                    }
                });
            });
            if (testQuestionCounter > 0) {
                if (this.props.subSectionDetails.data.settings === null || this.props.subSectionDetails.data.settings.testSettings === null) {
                    throw new Error("Test section should have settings");
                }
                const testSettings = this.props.subSectionDetails.data.settings.testSettings;
                if (testSettings.maxWrongAnswers !== 0 && wrongAnswersCounter > testSettings.maxWrongAnswers) {
                    incrementTestTriesForCaregiver(this.props.api.authInfo.caregiver.id);
                    const testTries = getTestTriesForCaregiver(this.props.api.authInfo.caregiver.id);
                    if (testSettings.testMaxTries > testTries) {
                        this.setState({
                            showTestPopUp: {
                                type: "MORE_TRIES",
                                text: testSettings.testRetryText
                            }
                        });
                        if (testSettings.shouldShowWrongAnswers) {
                           this.props.onTestTryAgain(this.props.subSectionDetails.data.id);
                        }
                    } else {
                        this.setState({
                            showTestPopUp: {
                                type: "FAIL",
                                text: testSettings.testFailText
                            }
                        });
                    }
                } else {
                    this.setState({
                        showTestPopUp: {
                            type: "SUCCESS",
                            text: testSettings.testSuccessText
                        }
                    });
                }
            } else {
                this.moveToNextPage();
            }
        }
    }

    handlePopUpConfirmClick() {
        this.setState({ showDocumentPopUp: null });
        this.props.onDocumentGeneratedStatusChange(this.props.subSectionDetails.data.id, true);
        this.props.onNextButtonClick();
    }

    handlePopUpCancelClick() {
        this.setState({ showDocumentPopUp: null });
    }

    onTestSuccess = () => {
        this.setState({ showTestPopUp: null });
        this.moveToNextPage();
    }

    onTestFail = () => {
        location.reload();
    }

    onTestMoreTries = () => {
        this.setState({ showTestPopUp: null });
    }

    moveToNextPage = async () => {
        if (this.props.subSectionDetails.document !== null) {

            // TODO remove when going back to popups
            this.setState({ documentUploadStatus: "LOADING" });
            try {
                const tempDocData = await this.props.api.getCaregiverGeneratedDocument(
                    this.props.subSectionDetails.data.id,
                    this.props.selectedCertification
                );
                await this.props.api.confirmCaregiverDocument(this.props.subSectionDetails.data.id, {
                    bucket: tempDocData.bucket,
                    key: tempDocData.key,
                    docType: tempDocData.docType
                });
                this.props.onDocumentGeneratedStatusChange(this.props.subSectionDetails.data.id, true);
                this.setState({ documentUploadStatus: null });
                this.props.onNextButtonClick();
            } catch (e) {
                this.setState({ documentUploadStatus: "ERROR" });
            }

            // TODO, this is still here cause omer said to we might come back to it soon
            // this.setState({ showDocumentPopUp: this.props.subSectionDetails.document });
        } else {
            this.props.onNextButtonClick();
        }
    }

    getTestPopUpTitle(type: "FAIL" | "SUCCESS" | "MORE_TRIES") {
        switch (type) {
            case "FAIL":
                return "Unmet requirements";
            case "SUCCESS":
                return "Congratulations!";
            case "MORE_TRIES":
                return "Not enough correct answers";
            default:
                return assertNever(type);
        }
    }

    getTestPopUpButton(type: "FAIL" | "SUCCESS" | "MORE_TRIES") {
        switch (type) {
            case "FAIL":
                return (
                    <Button
                        intent={Intent.DANGER}
                        onClick={this.onTestFail}
                        text="Log out"
                    />
                );
            case "SUCCESS":
                return (
                    <Button
                        intent={Intent.PRIMARY}
                        onClick={this.onTestSuccess}
                        text="Next!"
                    />
                );
            case "MORE_TRIES":
                return (
                    <Button
                        intent={Intent.PRIMARY}
                        onClick={this.onTestMoreTries}
                        text="Try again"
                    />
                );
            default:
                return assertNever(type);
        }
    }

    renderTestPopUp(popUpDetails: { type: "FAIL" | "SUCCESS" | "MORE_TRIES", text: string }) {
        return (
            <Dialog
                isOpen={true}
                isCloseButtonShown={false}
                title={this.getTestPopUpTitle(popUpDetails.type)}
                canOutsideClickClose={false}
                canEscapeKeyClose={false}
                className={"SubSectionLinkComponent__dialog"}
            >
                <div className="bp3-dialog-body">
                    {popUpDetails.text}
                </div>
                <div className="bp3-dialog-footer">
                    <div className="bp3-dialog-footer-actions">
                        {this.getTestPopUpButton(popUpDetails.type)}
                    </div>
                </div>
            </Dialog>
        );
    }

    render() {
        return (
            <Element
                className={"SubSectionLinkComponent__stickyBoundary"}
                name={"" + this.props.subSectionDetails.data.id}
            >
                {this.state.showTestPopUp !== null ? this.renderTestPopUp(this.state.showTestPopUp) : null}
                {this.state.showDocumentPopUp === null ? null :
                    <DocumentOverlayActivity
                        api={this.props.api}
                        subSectionDocument={this.state.showDocumentPopUp}
                        subSectionId={this.props.subSectionDetails.data.id}
                        onConfirm={() => this.handlePopUpConfirmClick()}
                        onCancelClick={() => this.handlePopUpCancelClick()}
                        selectedCertification={this.props.selectedCertification}
                    />
                }
                <SubSectionLinkComponent
                    title={this.props.subSectionDetails.data.title}
                    status={this.props.subSectionDetails.status}
                    isClicked={this.props.isClicked}
                    onClick={this.props.onLinkToSubSectionClick}
                    id={this.props.subSectionDetails.data.id}
                    url={this.props.matchUrl}
                />
                <Route
                    path={this.props.matchUrl + "/" + this.props.subSectionDetails.data.id}
                    render={(props: any) =>
                        <GenericQuestionGroupList
                            showUnfinishedError={this.state.showUnfinishedError}
                            documentStatus={this.state.documentUploadStatus}
                            groups={this.props.subSectionDetails.groups}
                            onAnswerChange={(newVal, questionId, groupId) => this.handleAnswerChange(newVal, questionId, groupId)}
                            onTriggerEvaluateHierarchyStatus={this.props.onTriggerEvaluateHierarchyStatus}
                            onPayRatesChange={(rates) => this.submitCaregiverCertificationPayRates(rates)}
                            getCertificationPayRates={() => this.props.getCertificationPayRates()}
                            onAnswerBlur={this.props.onAnswerBlur}
                            onNextButtonClick={() => this.handleNextButtonClick()}
                            availabilitySettings={this.props.subSectionDetails.data.settings === null ? null : this.props.subSectionDetails.data.settings.availabilityPlaceholder}
                            api={this.props.api}
                        />
                    }
                />
            </Element>

        );
    }
}



// WEBPACK FOOTER //
// ./src/components/GenericSubSection.tsx